import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import InputAdornment from '@material-ui/core/InputAdornment';
import CircularProgress from '@material-ui/core/CircularProgress';
import BarChartIcon from '@material-ui/icons/BarChart';
import { green, blue, amber } from '@material-ui/core/colors';

const useStyles = makeStyles((theme) => ({
	header: {
		backgroundColor: '#FEF200',
		color: '#212121',
		paddingTop: theme.spacing(1.5),
		paddingBottom: theme.spacing(1.5)
	},
	title: {
		fontWeight: 700,
		fontSize: '1.1rem'
	},
	subtitle: {
		fontSize: '0.8rem',
		color: '#5f5f00'
	},
	desc: {
		marginBottom: theme.spacing(2),
		color: theme.palette.text.secondary
	},
	row: {
		display: 'flex',
		alignItems: 'center',
		marginBottom: theme.spacing(2)
	},
	iconWrap: {
		width: 36,
		height: 36,
		borderRadius: '50%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginRight: theme.spacing(1.5),
		flexShrink: 0
	},
	lowIcon: {
		backgroundColor: green[500]
	},
	mediumIcon: {
		backgroundColor: amber[700]
	},
	highIcon: {
		backgroundColor: blue[500]
	},
	label: {
		width: 72,
		fontWeight: 600,
		flexShrink: 0
	},
	field: {
		flex: 1
	}
}));

const isValidQty = (val) => val !== '' && val !== null && /^\d+$/.test(String(val).trim());

function StockLevelDialog({ open, item, language, onClose, onConfirm }) {
	const classes = useStyles();
	const lang = language || {};
	const product = item || {};
	const [low, setLow] = useState('');
	const [medium, setMedium] = useState('');
	const [high, setHigh] = useState('');
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		if (open && item) {
			setLow(item.stock_level_low_qty != null ? String(item.stock_level_low_qty) : '');
			setMedium(item.stock_level_medium_qty != null ? String(item.stock_level_medium_qty) : '');
			setHigh(item.stock_level_high_qty != null ? String(item.stock_level_high_qty) : '');
			setSubmitting(false);
		}
	}, [open, item]);

	const valid = isValidQty(low) && isValidQty(medium) && isValidQty(high);

	const handleConfirm = () => {
		if (!valid || submitting) {
			return;
		}
		setSubmitting(true);
		onConfirm({ low, medium, high }, () => {
			setSubmitting(false);
		});
	};

	const levels = [
		{ key: 'low', label: lang.stock_level_low, value: low, setValue: setLow, iconClass: classes.lowIcon, placeholder: '10' },
		{ key: 'medium', label: lang.stock_level_medium, value: medium, setValue: setMedium, iconClass: classes.mediumIcon, placeholder: '30' },
		{ key: 'high', label: lang.stock_level_high, value: high, setValue: setHigh, iconClass: classes.highIcon, placeholder: '60' }
	];

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
			<DialogTitle disableTypography className={classes.header}>
				<Typography className={classes.title}>{lang.stock_level_set_title}</Typography>
				<Typography className={classes.subtitle}>
					{product.product_name} {product.product_code ? '· ' + product.product_code : ''}
				</Typography>
			</DialogTitle>
			<DialogContent>
				<Typography className={classes.desc}>{lang.stock_level_set_desc}</Typography>
				{levels.map((lvl) => (
					<div key={lvl.key} className={classes.row}>
						<div className={`${classes.iconWrap} ${lvl.iconClass}`}>
							<BarChartIcon htmlColor="#ffffff" fontSize="small" />
						</div>
						<Typography className={classes.label}>{lvl.label}</Typography>
						<TextField
							className={classes.field}
							type="number"
							variant="outlined"
							size="small"
							placeholder={`${lang.stock_level_eg} ${lvl.placeholder}`}
							value={lvl.value}
							onChange={(e) => lvl.setValue(e.target.value)}
							InputProps={{
								endAdornment: <InputAdornment position="end">{lang.stock_level_units}</InputAdornment>,
								inputProps: { min: 0, step: 1 }
							}}
						/>
					</div>
				))}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={submitting}>
					{lang.cancel}
				</Button>
				<Button onClick={handleConfirm} color="primary" variant="contained" disabled={!valid || submitting}>
					{submitting ? <CircularProgress size={20} /> : lang.confirm}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default StockLevelDialog;
